// ตัวเรียก API — ส่ง cookie session อัตโนมัติ, error เป็นภาษาไทยจากเซิร์ฟเวอร์เสมอ
export class ApiError extends Error {
  constructor(public status: number, message: string, public code?: string, public details?: unknown) {
    super(message);
  }
}

const TIMEOUT_MS = 30_000;

async function request<T>(method: string, path: string, body?: unknown, init: RequestInit = {}): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  let res: Response;
  try {
    res = await fetch(`/api/v1${path}`, {
      method,
      credentials: 'same-origin',
      headers: body !== undefined && !(body instanceof FormData) ? { 'content-type': 'application/json' } : undefined,
      body: body === undefined ? undefined : body instanceof FormData ? body : JSON.stringify(body),
      signal: controller.signal,
      ...init,
    });
  } catch (e) {
    throw new ApiError(0, (e as Error).name === 'AbortError' ? 'เชื่อมต่อนานเกินไป กรุณาลองใหม่' : 'เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ ตรวจสอบอินเทอร์เน็ต');
  } finally {
    clearTimeout(timer);
  }
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  if (!res.ok) {
    const d = (data ?? {}) as { error?: string; code?: string; details?: unknown };
    if (res.status === 401 && !path.startsWith('/auth/login')) window.dispatchEvent(new Event('setcom:unauthorized'));
    throw new ApiError(res.status, d.error ?? `เกิดข้อผิดพลาด (${res.status})`, d.code, d.details);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
  upload: <T>(path: string, form: FormData) => request<T>('POST', path, form),
};

export function qs(params: Record<string, unknown>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
}

/** ข้อความ "สิ่งที่ยังขาด" จาก error 422 ของเซิร์ฟเวอร์ */
export function errorDetails(e: unknown): string[] {
  if (!(e instanceof ApiError)) return [];
  const d = e.details as { requirements?: { message: string }[]; missing?: string[]; fields?: { path: string; message: string }[] } | undefined;
  if (d?.requirements) return d.requirements.map((r) => r.message);
  if (d?.missing) return d.missing;
  if (d?.fields) return d.fields.map((f) => `${f.path}: ${f.message}`);
  return [];
}
